import {FC, useState} from "react";
import {Column, Row} from "../atoms/Layout.tsx";
import InputGroup from "../molecules/InputGroup.tsx";
import SelectInputGroup from "../molecules/SelectInputGroup.tsx";
import {Option} from "../atoms/SelectInput.tsx";
import Button from "../atoms/Button.tsx";
import {SiteType} from "../api/site.ts";

export interface BasicSiteInformation {
    name: string;
    postcode: string;
    purpose: string;
    operatingHours: string;
    type: SiteType;
}

interface Props {
    onSubmit: (info: BasicSiteInformation) => void;
}

const SITE_FUNCTIONS: Option[] = [
    { value: 'retail', label: 'Retail' },
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'residential', label: 'Residential' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'recreational', label: 'Recreational' },
    { value: 'office', label: 'Office' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'education', label: 'Education' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'hospitality', label: 'Hospitality' },
    { value: 'other', label: 'Other' }
]

const SITE_TYPES: Option[] = [
    { value: SiteType.PROPERTY_OWNER, label: 'Property Owner' },
    { value: SiteType.PROPERTY_MANAGER, label: 'Property Manager' },
    { value: SiteType.SERVICE_PROVIDER, label: 'Service Provider' }
]

const validateSiteName = (attemptedSubmit: boolean, name: string): string | undefined => {
    if (name.length > 255) return "Site name must not exceed 255 characters";
    if (name.length === 0 && attemptedSubmit) return "Site name is required";
}

const validatePostcode = (attemptedSubmit: boolean, postcode: string): string | undefined => {
    if (postcode.length > 20) return "Postcode must not exceed 20 characters";
    if (postcode.length === 0 && attemptedSubmit) return "Postcode is required";
}

const validateOperatingHours = (attemptedSubmit: boolean, operatingHours: string): string | undefined => {
    if (operatingHours.length > 255) return "Operating hours must not exceed 255 characters";
    if (operatingHours.length === 0 && attemptedSubmit) return "Operating hours are required";
}

interface PurposeErrors {
    purposeError?: string;
    otherPurposeError?: string;
}

const validatePurpose = (attemptedSubmit: boolean, purpose: string | undefined, otherPurpose: string): PurposeErrors => {
    if (purpose === 'other' && otherPurpose.length === 0 && attemptedSubmit) {
        return { otherPurposeError: "Please specify the function of the site" };
    }

    if (purpose === undefined && attemptedSubmit) {
        return { purposeError: "Site function is required" }
    }

    if (purpose === 'other' && otherPurpose.length > 255) {
        return { otherPurposeError: "Site function must not exceed 255 characters" }
    }

    return {};
}

const SiteBasicInfoForm: FC<Props> = ({ onSubmit }) => {
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);

    const [name, setName] = useState('');
    const [postcode, setPostcode] = useState('');
    const [purpose, setPurpose] = useState<string | undefined>();
    const [otherPurpose, setOtherPurpose] = useState('');
    const [operatingHours, setOperatingHours] = useState('');
    const [type, setType] = useState(SiteType.PROPERTY_OWNER);

    const handleSubmit = () => {
        const purposeValidation = validatePurpose(true, purpose, otherPurpose)
        if (validateSiteName(true, name)
                || validatePostcode(true, postcode)
                || validateOperatingHours(true, operatingHours)
                || purposeValidation.purposeError
                || purposeValidation.otherPurposeError) {
            setAttemptedSubmit(true);
            return;
        }
        const finalPurpose = purpose === 'other' ? otherPurpose : purpose;
        onSubmit({
            name,
            postcode,
            purpose: finalPurpose || 'other',
            operatingHours,
            type
        });
    }

    return <Column gap="20">
        <InputGroup label="Site name" value={name} onChange={setName} error={validateSiteName(attemptedSubmit, name)} />
        <InputGroup label="Postcode of site" value={postcode} onChange={setPostcode} error={validatePostcode(attemptedSubmit, postcode)} />
        <SelectInputGroup label="What's the function of the site" options={SITE_FUNCTIONS} value={purpose} onChange={setPurpose} error={validatePurpose(attemptedSubmit, purpose, otherPurpose).purposeError} />
        {purpose === 'other' && (
            <InputGroup
                label="Please specify the function of the site"
                value={otherPurpose}
                onChange={setOtherPurpose}
                placeholder="e.g. Data Center, Logistics Hub"
                error={validatePurpose(attemptedSubmit, purpose, otherPurpose).otherPurposeError}
            />
        )}
        <SelectInputGroup label="What's your role at this site?" options={SITE_TYPES} value={type} onChange={v => setType(v as SiteType)} />
        <InputGroup label="What are the operating hours" value={operatingHours} onChange={setOperatingHours} error={validateOperatingHours(attemptedSubmit, operatingHours)} placeholder="e.g. 9am - 5pm, 24/7" />
        <Row>
            <Button onClick={handleSubmit}>Continue</Button>
        </Row>
    </Column>
}

export default SiteBasicInfoForm;